import { ETICHETTA_VISTA } from "@/lib/caso";
import type { TipoDerivato } from "@/lib/types";

/* Il segno del caso: la traccia a rombi e l'etichetta «Il caso · N
   tappe» dei disegni del 15/10/2026 (`.n-cchip .tp` e `.nc-caso3 .rb` in
   uploads/Notizia articolo chiuso riservata-html.zip).

   Nato nella testata della pagina del dispaccio, sta qui perché lo usano
   anche le righe del rullo (/rullo e /ricerca), il flusso della prima
   pagina e la fascia «Altri casi aperti»: chi legge una riga deve
   ritrovare lo stesso segno sulla pagina che apre.

   Senza direttiva: lo rendono sia Server Component (le righe del rullo)
   sia componenti client (DispaccioView, il flusso). Disegno in classi,
   quindi i toni sono i token `mare`/`segnale` di `.oi-home`, la radice
   di tutte le rotte che lo mostrano. */

/** La traccia: un filo nel mare e un rombo per tappa fino a quattro,
 *  l'ultimo (la tappa più recente) pieno nel rame. Oltre quattro il
 *  disegno non cambia, lo dice il numero accanto.
 *
 *  Due misure, quelle del disegno: 64 nel chip della testata (rombi da 6
 *  a 60) e 68 nelle schede (rombi da 6 a 62, `larga`). */
export function TracciaRombi({
  pezzi,
  larga = false,
  className,
}: {
  pezzi: number;
  larga?: boolean;
  className?: string;
}) {
  const w = larga ? 68 : 64;
  const ultimo = larga ? 62 : 60;
  const k = Math.min(4, Math.max(2, pezzi));
  const passo = (ultimo - 6) / (k - 1);
  return (
    <svg
      viewBox={`0 0 ${w} 12`}
      aria-hidden
      focusable="false"
      className={`h-3 flex-none overflow-visible ${larga ? "w-[68px]" : "w-16"} ${className ?? ""}`}
    >
      <line x1="2" y1="6" x2={w - 2} y2="6" className="stroke-mare" strokeOpacity={0.45} strokeWidth={1} />
      {Array.from({ length: k }, (_, i) => {
        const x = 6 + i * passo;
        const ora = i === k - 1;
        return (
          <path
            key={i}
            d={`M${x} 2L${x + 4} 6 ${x} 10 ${x - 4} 6Z`}
            strokeWidth={larga ? 1.1 : 1.2}
            className={ora ? "fill-segnale stroke-segnale" : "fill-bg stroke-mare"}
          />
        );
      })}
    </svg>
  );
}

/** L'etichetta delle liste: rombi, «Il caso» (o «Report») nel rame e
 *  «· N tappe» nel mare. Niente sulla notizia, e niente se il tipo non è
 *  arrivato (cronologie a monte giù): meglio un segno in meno che uno
 *  falso.
 *
 *  `corpo` è la classe del corpo: di serie i 13 pixel del chip
 *  (`text-voce`); vuota, eredita quello della riga che la ospita.
 *
 *  Testo in linea e non `inline-flex`, di proposito: una scatola atomica
 *  apre un punto di a capo subito dopo di sé, e nella riga del rullo il
 *  `·` che segue finiva in testa alla riga dopo (la classificazione è
 *  scritta perché un separatore non apra mai una riga). In linea e
 *  `whitespace-nowrap`, l'etichetta non si spezza e non si stacca da
 *  quello che le sta attaccato; i rombi stanno a metà altezza del testo
 *  con `align-middle`. Dentro un genitore flex diventa comunque un
 *  elemento flex, e non cambia niente. */
export function TappeCaso({
  tipo,
  tappe,
  corpo = "text-voce",
  className,
}: {
  tipo: TipoDerivato | null | undefined;
  tappe: number | null | undefined;
  corpo?: string;
  className?: string;
}) {
  if (tipo !== "caso" && tipo !== "report") return null;
  const n = tappe ?? 0;
  const etichetta = ETICHETTA_VISTA[tipo];
  return (
    <span
      className={`whitespace-nowrap font-sans font-semibold normal-case tracking-normal ${corpo} ${className ?? ""}`}
    >
      <TracciaRombi pezzi={n} className="mr-2 inline-block align-middle" />
      <span>
        <span className="text-segnale">{etichetta}</span>
        {n >= 2 ? <span className="text-mare"> · {n} tappe</span> : null}
      </span>
    </span>
  );
}
